import { useEffect, useState } from "react";
import { Main } from "@/components/Main";
import { ApiDataProps } from "@/pages/Tenants";
import { TypePageActions } from "@/pages/Plans";
import { CreateLoaderTable } from "@/components/Loaders";
import { LogProps } from "@/types/log";
import { TableLogs } from "@/App/Logs/Table";
import { LogFilters } from "@/App/Logs/Filters";
import { getLogsData, searchLogsData } from "@/Api/logs";
import { ActivitySquare } from "lucide-react";
import { useTypeUser } from "@/hooks/auth";
import { NotFound } from "../NotFound";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { PageHeader } from "@/components/PageHeader";

export const PageList = () => {
  // Only allow client type (2) to access this page
  const authOnlyClient = useTypeUser(2);

  const [logsData, setLogsData] = useState({} as ApiDataProps<LogProps>);
  const [state, setState] = useState("loading");
  const { hasPermission } = usePermissions();

  useEffect(() => {
    async function fetchData() {
      try {
        const { data } = await getLogsData();
        setLogsData(data?.response);
      } catch (error) {
        console.error("Error fetching logs:", error);
      } finally {
        setState("production");
      }
    }

    fetchData();
  }, []);

  const pageActions: TypePageActions = {
    next: async () => {
      if (logsData?.current_page < logsData?.last_page) {
        setState("loading");

        try {
          const { data } = await getLogsData(logsData?.current_page + 1);
          setLogsData(data.response);
        } catch (error) {
          console.error("Error fetching next page:", error);
        } finally {
          setState("production");
        }
      }
    },
    previous: async () => {
      if (logsData?.current_page > 1) {
        setState("loading");

        try {
          const { data } = await getLogsData(logsData?.current_page - 1);
          setLogsData(data.response);
        } catch (error) {
          console.error("Error fetching previous page:", error);
        } finally {
          setState("production");
        }
      }
    },
    lastPage: async () => {
      if (logsData?.current_page !== logsData?.last_page) {
        setState("loading");

        try {
          const { data } = await getLogsData(logsData?.last_page);
          setLogsData(data?.response);
        } catch (error) {
          console.error("Error fetching last page:", error);
        } finally {
          setState("production");
        }
      }
    },
  };

  const handleSearch = async (params: any) => {
    return searchLogsData(params);
  };

  return (
    authOnlyClient(() => (
      <Main>
        <If condition={hasPermission("view-activity-log-list")}>
          <PageHeader
            icon={ActivitySquare}
            title="Registro de Atividades"
            description="Acompanhe as atividades do sistema"
          />

          <div className="flex flex-col gap-4">
            <LogFilters
              updateFront={setLogsData}
              setState={setState}
              funcSearch={handleSearch}
            />

            {state === "loading" ? (
              <CreateLoaderTable cols={8} lines={10} />
            ) : (
              <TableLogs
                logs={logsData?.data || []}
                dataApi={logsData}
                pageActions={pageActions}
              />
            )}
          </div>
        </If>
      </Main>
    )) || <NotFound />
  );
};
